import { sortStringsAlphabetically } from './sorting'
import { capitalize } from './string'
import i18next from '../i18n/i18n'
import { ISocietyEntranceModel } from '../interfaces/models/society-entrance.interfaces'
import { IUnitModel } from '../interfaces/models/units.interfaces'

export interface ExtendedUnitModel extends IUnitModel {
  entrance: ISocietyEntranceModel
}

export const groupUnitsPerAddress = (
  units: ExtendedUnitModel[]
): { address: string; units: IUnitModel[] }[] => {
  const sortedUnitsPerAddress = units.reduce(
    (
      prev: {
        [key: string]: IUnitModel[]
      },
      unit
    ) => {
      if (!unit.entrance) {
        return prev
      }

      const address = unit.entrance.addressStreet
      if (address) {
        if (!(address in prev)) {
          // eslint-disable-next-line no-param-reassign
          prev[address] = [unit]
        } else {
          prev[address].push(unit)
        }
      }

      return prev
    },
    {}
  )

  // Return array sorted by address
  return Object.keys(sortedUnitsPerAddress)
    .map((key) => ({
      address: key,
      units: sortedUnitsPerAddress[key],
    }))
    .sort((a, b) => sortStringsAlphabetically(a.address, b.address))
}

export const getUnitString = (
  unit: ExtendedUnitModel | undefined,
  isSamf?: boolean | undefined,
  managementMode?: boolean | undefined
): string | null => {
  if (!unit) {
    return null
  }
  if (unit.isUnassigned) {
    return i18next.t('common.unit.unassigned')
  }

  const entranceAddressStreet = unit.entrance?.addressStreet
    ? capitalize(unit.entrance?.addressStreet)
    : undefined
  const unitTitleLegal = unit.titleLegal
    ? capitalize(unit.titleLegal.toLowerCase())
    : undefined
  const unitTitle = capitalize(unit.title.toLowerCase())

  const getBrfUnitString = (): string => {
    if (unitTitle && unitTitleLegal) {
      if (unitTitle === unitTitleLegal) {
        return unitTitle
      }
      return `${unitTitleLegal} (${unitTitle})`
    }
    return [unitTitle, unitTitleLegal]
      .filter((_str) => _str !== undefined)
      .join(', ')
  }

  const getSamfUnitString = (): string => {
    if (!managementMode) {
      if (unitTitleLegal) {
        return `${unitTitle} (${unitTitleLegal})`
      }
      return `${unitTitle}`
    }
    if (unitTitleLegal) {
      return `${unitTitleLegal} (${unitTitle})`
    }
    return `${unitTitle}`
  }

  if (isSamf) {
    return getSamfUnitString()
  }
  return `${[entranceAddressStreet || undefined, getBrfUnitString()]
    .filter((_str) => _str !== undefined)
    .join(', ')}`
}

export const getFloorFromTitleLegal = (titleLegal?: string): number => {
  if (!titleLegal || titleLegal.length < 4) {
    return 0
  }
  return (parseInt(titleLegal[0], 10) - 1) * 10 + parseInt(titleLegal[1], 10)
}
